/**
 * KnativeServingWithPipelines - Knative Serving tab 组合
 *
 * - 状态条 + 流量/伸缩/Revisions 卡片（EntityKnativeServingContent）
 * - 流量卡 / Revisions 卡的主操作按钮（切换流量 / 新建版本，沿用原文案+图标）
 *   点击唤起 CD Pipeline 执行对话框，旁带最近执行状态点
 * - 工作流运行期间：顶部横幅提示「正在运行」，并让状态条/卡片静默自动刷新，
 *   完成后再续刷一小段时间确保抓到最终态，然后停止（用 hook 既有的 refreshIntervalMs，
 *   不改 hook 内部）
 * - 底部「运维应急」折叠区收纳直写入口（切换流量 / 新建版本，绕过工作流）
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Tooltip, CircularProgress, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { EntityKnativeServingContent } from '@internal/plugin-knative-serving';
import { useKnativePipelineActions } from './useKnativePipelineActions';
import { KnativeAdminZone } from './KnativeAdminZone';

const HINT = '点击唤起工作流执行；完整执行状态、阶段与日志请在「CD Pipelines」标签查看';
// 工作流运行期间卡片静默刷新间隔
const POLL_MS = 8000;
// 工作流结束后继续刷新的宽限时长，确保抓到 ksvc 最终态
const GRACE_MS = 15000;

export const KnativeServingWithPipelines: React.FC = () => {
  const {
    releasePipeline,
    trafficSplitPipeline,
    openReleaseDialog,
    openTrafficDialog,
    releaseRuns,
    trafficRuns,
    renderStatusDots,
    executingPipelines,
    dialogs,
  } = useKnativePipelineActions();

  const running = executingPipelines.size > 0;

  // 运行中开启轮询；运行结束后再续 GRACE_MS 以抓到最终态，然后关闭
  const [pollActive, setPollActive] = useState(false);
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (running) {
      if (graceTimer.current) {
        clearTimeout(graceTimer.current);
        graceTimer.current = null;
      }
      setPollActive(true);
      return undefined;
    }
    // running 刚结束：续刷一段时间
    if (pollActive && !graceTimer.current) {
      graceTimer.current = setTimeout(() => {
        setPollActive(false);
        graceTimer.current = null;
      }, GRACE_MS);
    }
    return undefined;
  }, [running, pollActive]);

  const refreshIntervalMs = pollActive ? POLL_MS : 0;

  // 流量卡按钮：切换流量 → 唤起切流 Pipeline
  const trafficCardHeaderAction = (
    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
      {renderStatusDots(trafficRuns, 5)}
      <Tooltip title={HINT}>
        <span>
          <Button
            size="small"
            color="primary"
            startIcon={<EditIcon />}
            onClick={openTrafficDialog}
            disabled={!trafficSplitPipeline}
            style={{ fontSize: '0.8rem' }}
          >
            切换流量
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  // Revisions 卡按钮：新建版本 → 唤起发版 Pipeline
  const revisionsCardHeaderAction = (
    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
      {renderStatusDots(releaseRuns, 5)}
      <Tooltip title={HINT}>
        <span>
          <Button
            size="small"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openReleaseDialog}
            disabled={!releasePipeline}
            style={{ fontSize: '0.8rem' }}
          >
            新建版本
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      {/* 工作流运行横幅：运行期间常驻，卡片同步静默刷新 */}
      {running && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          style={{ marginBottom: 12 }}
        >
          <Typography variant="body2">
            正在运行工作流（发版 / 切流）…下方流量与版本卡片将自动更新。
            完整阶段与日志见「CD Pipelines」标签。
          </Typography>
        </Alert>
      )}

      <EntityKnativeServingContent
        trafficCardHeaderAction={trafficCardHeaderAction}
        revisionsCardHeaderAction={revisionsCardHeaderAction}
        footer={<KnativeAdminZone />}
        refreshIntervalMs={refreshIntervalMs}
      />
      {dialogs}
    </Box>
  );
};
